import type { Capability, CapabilityLevel, Role } from '@/api/types.gen';
import { useMe } from './useMe';

// MIRROR of shared/src/capabilities.rs — keep in lockstep by hand until
// gen_ts.rs emits this matrix. The Rust matrix is the source of truth; this
// is a UX convenience. The API enforces every cap server-side.
// 3 roles × 24 caps = 72 cells. Update both sides together when caps change.

const MATRIX: Record<Role, Record<Capability, CapabilityLevel>> = {
  admin: {
    campaigns_view: 'full',
    campaigns_edit: 'full',
    campaigns_launch: 'full',
    audiences_view: 'full',
    audiences_edit: 'full',
    creatives_view: 'full',
    creatives_edit: 'full',
    reports_view: 'full',
    reports_export: 'full',
    backend_view: 'full',
    backend_edit: 'full',
    pixels_view: 'full',
    pixels_manage: 'full',
    pixel_samples_view: 'full',
    integrations_manage: 'full',
    users_manage: 'full',
    roles_manage: 'full',
    logs_view: 'full',
    audit_view: 'full',
    tokens_issue: 'full',
    tokens_revoke: 'full',
    pii_reveal: 'full',
    config_manage: 'full',
    billing_manage: 'full',
  },
  planner: {
    campaigns_view: 'full',
    campaigns_edit: 'full',
    campaigns_launch: 'full',
    audiences_view: 'full',
    audiences_edit: 'full',
    creatives_view: 'full',
    creatives_edit: 'full',
    reports_view: 'full',
    reports_export: 'full',
    backend_view: 'full',
    backend_edit: 'own',
    pixels_view: 'full',
    pixels_manage: 'none',
    pixel_samples_view: 'full',
    integrations_manage: 'none',
    users_manage: 'none',
    roles_manage: 'none',
    logs_view: 'none',
    audit_view: 'own',
    tokens_issue: 'own',
    tokens_revoke: 'own',
    pii_reveal: 'none',
    config_manage: 'none',
    billing_manage: 'none',
  },
  reporter: {
    campaigns_view: 'full',
    campaigns_edit: 'none',
    campaigns_launch: 'none',
    audiences_view: 'full',
    audiences_edit: 'none',
    creatives_view: 'full',
    creatives_edit: 'none',
    reports_view: 'full',
    reports_export: 'full',
    backend_view: 'full',
    backend_edit: 'none',
    // Config, snippet, activity, export — not the captured payloads. Reporter
    // is the only role with pii_reveal 'none'; pixel_samples_view is the other
    // door onto customer data.
    pixels_view: 'full',
    pixels_manage: 'none',
    pixel_samples_view: 'none',
    integrations_manage: 'none',
    users_manage: 'none',
    roles_manage: 'none',
    logs_view: 'none',
    audit_view: 'none',
    tokens_issue: 'own',
    tokens_revoke: 'own',
    pii_reveal: 'none',
    config_manage: 'none',
    billing_manage: 'none',
  },
};

// Order matches shared crate semantics. `view` and `own` are siblings — neither
// strictly implies the other; pick the right `min` for each call site.
const RANK: Record<CapabilityLevel, number> = {
  none: 0,
  own: 1,
  view: 2,
  full: 3,
};

export function can(role: Role, cap: Capability): CapabilityLevel {
  return MATRIX[role]?.[cap] ?? 'none';
}

export function canAtLeast(role: Role, cap: Capability, min: CapabilityLevel): boolean {
  return RANK[can(role, cap)] >= RANK[min];
}

export function useCan(cap: Capability, min: CapabilityLevel = 'view'): boolean {
  const { data: me } = useMe();
  return me ? canAtLeast(me.role, cap, min) : false;
}
